import { useCallback, useEffect, useState, useRef, lazy, Suspense } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useSearchEvents, type SearchParams } from '../hooks/usePotlucks'
import { useAuthStore } from '../store/auth'
import { geocodeAddress, reverseGeocode } from '../lib/geocode'
import { PublicLayout } from '../components/layout/PublicLayout'
import { EVENT_TYPES, GRADIENT } from '../components/ui/eventTypes'
import OccurrenceCard from '../components/OccurrenceCard'
import { useIsMobile } from '../hooks/useIsMobile'
import { useCurrentPosition } from '../hooks/useCurrentPosition'
import type { OccurrenceWithEvent } from '@fellowship/shared'
import {
  MapPin,
  Search,
  SlidersHorizontal,
  X,
  ChevronDown,
  Map,
  List,
  Check,
} from 'lucide-react'
import { WhenPicker } from '../components/ui/WhenPicker'

const PotluckMap = lazy(() => import('../components/PotluckMap'))

const DEFAULT_RADIUS = 25

const DIETARY_OPTIONS = [
  { key: 'vegetarianFriendly', label: 'Vegetarian' },
  { key: 'veganFriendly', label: 'Vegan' },
  { key: 'noMeat', label: 'No meat' },
  { key: 'noAlcohol', label: 'No alcohol' },
] as const

const AUDIENCE_OPTIONS = [
  { key: 'kidFriendly', label: 'Kid friendly' },
  { key: 'allWelcome', label: 'All welcome' },
  { key: 'accessibilityFriendly', label: 'Accessible' },
] as const

const DENOMINATION_OPTIONS = [
  { value: 'seventh-day-adventist', label: 'Seventh-day Adventist' },
  { value: 'baptist', label: 'Baptist' },
  { value: 'methodist', label: 'Methodist' },
  { value: 'catholic', label: 'Catholic' },
  { value: 'non-denominational', label: 'Non-denominational' },
  { value: 'lutheran', label: 'Lutheran' },
  { value: 'presbyterian', label: 'Presbyterian' },
]

const SORT_OPTIONS = [
  { value: 'closest', label: 'Closest first' },
  { value: 'soonest', label: 'Soonest first' },
  { value: 'popular', label: 'Most popular' },
  { value: 'newest', label: 'Newest' },
] as const

type SortValue = 'closest' | 'soonest' | 'popular' | 'newest'

const ATTR_LABELS: Record<string, string> = {
  ...Object.fromEntries(DIETARY_OPTIONS.map((o) => [o.key, o.label])),
  ...Object.fromEntries(AUDIENCE_OPTIONS.map((o) => [o.key, o.label])),
}

// ─── FilterCheckRow ───────────────────────────────────────────────────────────

function FilterCheckRow({
  label,
  checked,
  onToggle,
}: {
  label: string
  checked: boolean
  onToggle: () => void
}) {
  return (
    <div
      onClick={onToggle}
      style={{
        padding: '7px 8px',
        borderRadius: 8,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        userSelect: 'none',
        background: checked ? 'var(--primary-soft)' : 'transparent',
      }}
    >
      <div
        style={{
          width: 16,
          height: 16,
          borderRadius: 4,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: checked ? 'var(--primary)' : 'transparent',
          border: checked ? 'none' : '1.5px solid var(--line)',
        }}
      >
        {checked && <Check size={10} color="#fff" strokeWidth={3} />}
      </div>
      <span
        style={{
          fontSize: 13,
          color: checked ? 'var(--primary-deep)' : 'var(--ink)',
          fontWeight: checked ? 500 : 400,
        }}
      >
        {label}
      </span>
    </div>
  )
}

// ─── Mobile When presets ─────────────────────────────────────────────────────

function buildMobileWhenPresets() {
  const now = new Date()
  const tod = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  const dow = tod.getDay()
  const daysToSat = dow === 0 ? 6 : 6 - dow
  const sat = new Date(tod.getFullYear(), tod.getMonth(), tod.getDate() + daysToSat)
  const sun = new Date(sat.getFullYear(), sat.getMonth(), sat.getDate() + 1)
  const mon = new Date(tod.getFullYear(), tod.getMonth(), tod.getDate() + (dow === 0 ? -6 : 1 - dow))
  const weekEnd = new Date(mon.getFullYear(), mon.getMonth(), mon.getDate() + 6)
  return [
    { label: 'Any time', from: '', to: '' },
    { label: 'Today', from: fmt(tod), to: fmt(tod) },
    { label: 'This weekend', from: fmt(sat), to: fmt(sun) },
    { label: 'This week', from: fmt(mon), to: fmt(weekEnd) },
    { label: 'This month', from: fmt(new Date(tod.getFullYear(), tod.getMonth(), 1)), to: fmt(new Date(tod.getFullYear(), tod.getMonth() + 1, 0)) },
  ]
}

// ─── FiltersPopover ───────────────────────────────────────────────────────────

interface FiltersPopoverProps {
  anchorRef: React.RefObject<HTMLButtonElement | null>
  dietary: Set<string>
  audience: Set<string>
  denomination: string
  resultCount: number
  onApply: (dietary: Set<string>, audience: Set<string>, denomination: string) => void
  onClose: () => void
  isMobile?: boolean
  from?: string
  to?: string
  onWhenChange?: (from: string, to: string) => void
}

function FiltersPopover({
  anchorRef,
  dietary,
  audience,
  denomination,
  resultCount,
  onApply,
  onClose,
  isMobile,
  from: fromStr = '',
  to: toStr = '',
  onWhenChange,
}: FiltersPopoverProps) {
  const [pendingDietary, setPendingDietary] = useState(() => new Set(dietary))
  const [pendingAudience, setPendingAudience] = useState(() => new Set(audience))
  const [pendingDenomination, setPendingDenomination] = useState(denomination)
  const popoverRef = useRef<HTMLDivElement>(null)
  const mobilePresets = buildMobileWhenPresets()

  // Outside-click: desktop only (mobile uses backdrop)
  useEffect(() => {
    if (isMobile) return
    const handler = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(e.target as Node)
      )
        onClose()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [anchorRef, onClose, isMobile])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  function toggleSet(setter: React.Dispatch<React.SetStateAction<Set<string>>>, key: string) {
    setter((prev) => {
      const n = new Set(prev)
      n.has(key) ? n.delete(key) : n.add(key)
      return n
    })
  }

  const pendingCount = pendingDietary.size + pendingAudience.size + (pendingDenomination ? 1 : 0)

  const filterSections = (
    <>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--muted)',
          marginBottom: 6,
        }}
      >
        Dietary
      </div>
      {DIETARY_OPTIONS.map((opt) => (
        <FilterCheckRow
          key={opt.key}
          label={opt.label}
          checked={pendingDietary.has(opt.key)}
          onToggle={() => toggleSet(setPendingDietary, opt.key)}
        />
      ))}

      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--muted)',
          margin: '14px 0 6px',
        }}
      >
        Audience
      </div>
      {AUDIENCE_OPTIONS.map((opt) => (
        <FilterCheckRow
          key={opt.key}
          label={opt.label}
          checked={pendingAudience.has(opt.key)}
          onToggle={() => toggleSet(setPendingAudience, opt.key)}
        />
      ))}

      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--muted)',
          margin: '14px 0 8px',
        }}
      >
        Denomination
      </div>
      <select
        value={pendingDenomination}
        onChange={(e) => setPendingDenomination(e.target.value)}
        aria-label="Denomination filter"
        className="input"
        style={{ marginBottom: 8, cursor: 'pointer' }}
      >
        <option value="">Any denomination</option>
        {DENOMINATION_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </>
  )

  const filterFooter = (
    <div
      style={{
        padding: isMobile ? '12px 20px' : '12px 16px',
        borderTop: '1px solid var(--line-soft)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: 'var(--surface-2)',
      }}
    >
      <button
        onClick={() => {
          setPendingDietary(new Set())
          setPendingAudience(new Set())
          setPendingDenomination('')
          if (onWhenChange) onWhenChange('', '')
        }}
        style={{
          background: 'transparent',
          border: 0,
          fontSize: 12,
          color: 'var(--muted)',
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        Clear all
      </button>
      <button
        onClick={() => {
          onApply(pendingDietary, pendingAudience, pendingDenomination)
          onClose()
        }}
        style={{
          height: 34,
          padding: '0 16px',
          borderRadius: 999,
          background: GRADIENT,
          color: '#fff',
          border: 0,
          cursor: 'pointer',
          fontSize: 12.5,
          fontWeight: 600,
          fontFamily: 'inherit',
        }}
      >
        {pendingCount === 0
          ? 'Show results'
          : `Show ${resultCount} event${resultCount !== 1 ? 's' : ''}`}
      </button>
    </div>
  )

  // ── Mobile: fixed bottom sheet ────────────────────────────────────────────
  if (isMobile) {
    return (
      <>
        <div
          onClick={onClose}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 99 }}
        />
        <div
          ref={popoverRef}
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 100,
            background: 'var(--surface)',
            borderRadius: '20px 20px 0 0',
            maxHeight: '85vh',
            overflowY: 'auto',
            boxShadow: '0 -8px 40px rgba(31,26,46,.18)',
          }}
        >
          {/* Drag handle */}
          <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 4px' }}>
            <div style={{ width: 36, height: 4, borderRadius: 999, background: 'var(--line)' }} />
          </div>

          <div style={{ padding: '4px 20px 0' }}>
            {/* When section */}
            {onWhenChange && (
              <>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'var(--muted)',
                    margin: '10px 0 10px',
                  }}
                >
                  When
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 4 }}>
                  {mobilePresets.map((p) => {
                    const isActive =
                      fromStr === p.from && toStr === p.to
                    return (
                      <button
                        key={p.label}
                        onClick={() => onWhenChange(p.from, p.to)}
                        style={{
                          height: 34,
                          padding: '0 14px',
                          borderRadius: 999,
                          border: `1.5px solid ${isActive ? 'var(--primary)' : 'var(--line)'}`,
                          background: isActive ? 'var(--primary-soft)' : 'transparent',
                          color: isActive ? 'var(--primary-deep)' : 'var(--ink-2)',
                          fontSize: 13,
                          fontWeight: isActive ? 600 : 400,
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                        }}
                      >
                        {p.label}
                      </button>
                    )
                  })}
                </div>
              </>
            )}

            <div style={{ marginTop: 14 }}>{filterSections}</div>
          </div>

          {filterFooter}
        </div>
      </>
    )
  }

  // ── Desktop: absolute popover ─────────────────────────────────────────────
  return (
    <div
      ref={popoverRef}
      style={{
        position: 'absolute',
        top: '100%',
        right: 0,
        marginTop: 8,
        width: 360,
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: 16,
        zIndex: 100,
        overflow: 'hidden',
        boxShadow: '0 24px 48px -16px rgba(31,26,46,.22), 0 4px 10px -4px rgba(31,26,46,.12)',
      }}
    >
      <div style={{ padding: '16px 16px 4px' }}>{filterSections}</div>
      {filterFooter}
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { user } = useAuthStore()
  const isMobile = useIsMobile()

  const initLat = parseFloat(searchParams.get('lat') ?? '') || (user?.locationLat ?? 34.0522)
  const initLng = parseFloat(searchParams.get('lng') ?? '') || (user?.locationLng ?? -118.2437)
  const initRadius = parseInt(searchParams.get('radius') ?? '') || DEFAULT_RADIUS
  const initQ = searchParams.get('q') ?? ''
  const initFrom = searchParams.get('from') ?? ''
  const initTo = searchParams.get('to') ?? ''
  const initTypes = searchParams.get('types')?.split(',').filter(Boolean) ?? []
  const initDietary = new Set(searchParams.get('dietary')?.split(',').filter(Boolean) ?? [])
  const initAudience = new Set(searchParams.get('audience')?.split(',').filter(Boolean) ?? [])
  const validDenominations = new Set(DENOMINATION_OPTIONS.map((o) => o.value))
  const initDenomination = searchParams.get('denomination') ??
    (user?.denomination && validDenominations.has(user.denomination) ? user.denomination : '')
  const initSort = (searchParams.get('sort') as SortValue | null) ?? 'closest'

  const [lat, setLat] = useState(initLat)
  const [lng, setLng] = useState(initLng)
  const [radius, setRadius] = useState(initRadius)
  const [from, setFrom] = useState(initFrom)
  const [to, setTo] = useState(initTo)
  const [locationText, setLocationText] = useState(initQ || user?.locationLabel || '')
  const [selectedId, setSelectedId] = useState<string | undefined>()
  const [view, setView] = useState<'map' | 'list'>(() =>
    window.matchMedia?.('(max-width: 767px)').matches ? 'list' : 'map'
  )
  const [activeTypes, setActiveTypes] = useState<string[]>(initTypes.length ? initTypes : ['all'])
  const [dietary, setDietary] = useState<Set<string>>(initDietary)
  const [audience, setAudience] = useState<Set<string>>(initAudience)
  const [denomination, setDenomination] = useState(initDenomination)
  const [sort, setSort] = useState<SortValue>(initSort)
  const [textFilter, setTextFilter] = useState('')
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [sortOpen, setSortOpen] = useState(false)
  const currentPosition = useCurrentPosition()
  const currentPositionApplied = useRef(false)
  const filtersButtonRef = useRef<HTMLButtonElement>(null)
  const sortRef = useRef<HTMLDivElement>(null)
  const didGeocode = useRef(false)

  useEffect(() => {
    if (didGeocode.current) return
    didGeocode.current = true
    const q = searchParams.get('q')
    if (!q) return
    geocodeAddress(q).then((coords) => {
      if (coords) {
        setLat(coords.lat)
        setLng(coords.lng)
      }
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Current position wins over profile location (highest priority after URL params)
  useEffect(() => {
    if (searchParams.get('lat') || searchParams.get('lng')) return
    if (!currentPosition) return
    currentPositionApplied.current = true
    setLat(currentPosition.lat)
    setLng(currentPosition.lng)
    if (currentPosition.label && !searchParams.get('q')) setLocationText(currentPosition.label)
  }, [currentPosition]) // eslint-disable-line react-hooks/exhaustive-deps

  // Profile location fallback — only if geolocation didn't fire
  useEffect(() => {
    if (searchParams.get('lat') || searchParams.get('lng')) return
    if (currentPositionApplied.current) return
    if (user?.locationLat && user?.locationLng) {
      setLat(user.locationLat)
      setLng(user.locationLng)
      if (user.locationLabel && !searchParams.get('q')) setLocationText(user.locationLabel)
    }
  }, [user?.locationLat, user?.locationLng]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!sortOpen) return
    const handler = (e: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) setSortOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [sortOpen])

  const attributeKeys = [...dietary, ...audience]
  const attributeFilterCount = dietary.size + audience.size + (denomination ? 1 : 0)
  const hasActiveFilters = attributeFilterCount > 0

  const activeFilterChips = [
    ...Array.from(dietary).map((k) => ({
      key: k,
      label: ATTR_LABELS[k] ?? k,
      onRemove: () =>
        setDietary((prev) => {
          const n = new Set(prev)
          n.delete(k)
          return n
        }),
    })),
    ...Array.from(audience).map((k) => ({
      key: k,
      label: ATTR_LABELS[k] ?? k,
      onRemove: () =>
        setAudience((prev) => {
          const n = new Set(prev)
          n.delete(k)
          return n
        }),
    })),
    ...(denomination
      ? [
          {
            key: 'denomination',
            label:
              DENOMINATION_OPTIONS.find((o) => o.value === denomination)?.label ?? denomination,
            onRemove: () => setDenomination(''),
          },
        ]
      : []),
  ]

  const queryParams: SearchParams = {
    lat,
    lng,
    radius,
    from: from || undefined,
    to: to || undefined,
    eventTypes: activeTypes.includes('all') ? undefined : activeTypes.join(','),
    attributes: attributeKeys.length > 0 ? attributeKeys.join(',') : undefined,
    denomination: denomination || undefined,
    sort,
  }

  const { data, isLoading, isError } = useSearchEvents(queryParams)
  const occurrences = data?.data?.occurrences ?? []
  const total = data?.meta?.total ?? 0

  const needle = textFilter.trim().toLowerCase()
  const visibleOccurrences = needle
    ? occurrences.filter((occ) => {
        const title = (occ.overrideTitle ?? occ.event.title).toLowerCase()
        const location = (occ.event.locationName ?? occ.event.addressText).toLowerCase()
        return title.includes(needle) || location.includes(needle)
      })
    : occurrences

  useEffect(() => {
    const dietaryArr = Array.from(dietary)
    const audienceArr = Array.from(audience)
    setSearchParams(
      {
        lat: String(lat),
        lng: String(lng),
        radius: String(radius),
        ...(locationText ? { q: locationText } : {}),
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
        ...(activeTypes.includes('all') ? {} : { types: activeTypes.join(',') }),
        ...(dietaryArr.length ? { dietary: dietaryArr.join(',') } : {}),
        ...(audienceArr.length ? { audience: audienceArr.join(',') } : {}),
        ...(denomination ? { denomination } : {}),
        ...(sort !== 'closest' ? { sort } : {}),
      },
      { replace: true },
    )
  }, [lat, lng, radius, locationText, from, to, activeTypes, dietary, audience, denomination, sort]) // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSearch() {
    if (!locationText.trim()) return
    const coords = await geocodeAddress(locationText)
    if (coords) {
      setLat(coords.lat)
      setLng(coords.lng)
    }
  }

  const [locating, setLocating] = useState(false)

  function handleLocate() {
    if (!navigator.geolocation) return
    setLocating(true)
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords
        setLat(latitude)
        setLng(longitude)
        const label = await reverseGeocode(latitude, longitude)
        if (label) setLocationText(label)
        setLocating(false)
      },
      () => setLocating(false),
      { timeout: 8000 },
    )
  }

  const handleSelect = useCallback((occ: OccurrenceWithEvent) => setSelectedId(occ.id), [])

  function toggleType(k: string) {
    if (k === 'all') return setActiveTypes(['all'])
    const without = activeTypes.filter((x) => x !== 'all' && x !== k)
    const next = activeTypes.includes(k) ? without : [...without, k]
    setActiveTypes(next.length ? next : ['all'])
  }

  const currentSortLabel = SORT_OPTIONS.find((o) => o.value === sort)?.label ?? 'Closest first'

  return (
    <PublicLayout activeNav="Discover" hideFooter>
      {/* ── 1 · Search toolbar row ───────────────────────────────────────────── */}
      <div
        style={{
          padding: isMobile ? '8px 12px' : '10px 32px',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          borderBottom: '1px solid var(--line-soft)',
          background: 'var(--bg)',
          flexShrink: 0,
        }}
      >
        {/* Search pill */}
        <div
          style={{
            flex: 1,
            maxWidth: isMobile ? 'none' : 520,
            display: 'flex',
            alignItems: 'center',
            height: 44,
            padding: 4,
            background: 'var(--surface)',
            border: '1px solid var(--line-soft)',
            borderRadius: 999,
            boxShadow: '0 2px 12px rgba(31,26,46,.06)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '0 12px',
              flex: isMobile ? 1 : '1.2',
            }}
          >
            <button
              type="button"
              onClick={handleLocate}
              disabled={locating}
              title="Use my current location"
              style={{
                flexShrink: 0,
                border: 0,
                background: 'transparent',
                padding: 0,
                cursor: locating ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                color: locating ? 'var(--primary)' : 'var(--muted)',
                opacity: locating ? 0.6 : 1,
              }}
            >
              <MapPin size={13} style={{ flexShrink: 0 }} />
            </button>
            <div style={{ flex: 1, minWidth: 0 }}>
              {!isMobile && (
                <div
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: 'var(--muted)',
                    lineHeight: 1,
                  }}
                >
                  Where
                </div>
              )}
              <input
                value={locationText}
                onChange={(e) => setLocationText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="City or address"
                style={{
                  border: 0,
                  background: 'transparent',
                  outline: 'none',
                  fontWeight: 500,
                  fontSize: 13,
                  fontFamily: 'inherit',
                  color: 'var(--ink)',
                  width: '100%',
                }}
              />
            </div>
          </div>
          {!isMobile && (
            <>
              <div style={{ width: 1, height: 22, background: 'var(--line)', flexShrink: 0 }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 12px', flex: 1 }}>
                <WhenPicker
                  from={from}
                  to={to}
                  onChange={(f, t) => { setFrom(f); setTo(t) }}
                />
              </div>
            </>
          )}
          <button
            onClick={handleSearch}
            style={{
              height: 36,
              padding: isMobile ? '0 14px' : '0 18px',
              borderRadius: 999,
              background: GRADIENT,
              color: '#fff',
              border: 0,
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontFamily: 'inherit',
              flexShrink: 0,
              boxShadow: '0 4px 12px rgba(59,76,202,.3)',
            }}
          >
            <Search size={13} />
            {!isMobile && ' Search'}
          </button>
        </div>

        {/* Map / List toggle */}
        <button
          onClick={() => setView(view === 'map' ? 'list' : 'map')}
          style={{
            height: 36,
            padding: '0 14px',
            borderRadius: 999,
            background: 'transparent',
            border: '1.5px solid var(--line)',
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 500,
            color: 'var(--ink)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontFamily: 'inherit',
            flexShrink: 0,
          }}
        >
          {view === 'map' ? (
            <>
              <List size={14} /> List
            </>
          ) : (
            <>
              <Map size={14} /> Map
            </>
          )}
        </button>
      </div>

      {/* ── 2 · Type pill + filter row ───────────────────────────────────────── */}
      <div
        style={{
          padding: isMobile ? '8px 12px' : '10px 32px',
          display: 'flex',
          gap: 6,
          alignItems: 'center',
          borderBottom: '1px solid var(--line-soft)',
          flexShrink: 0,
          background: 'var(--bg)',
          flexWrap: isMobile ? 'nowrap' : 'wrap',
          overflowX: isMobile ? 'auto' : 'visible',
          scrollbarWidth: 'none',
        }}
      >
        {/* All pill */}
        <button
          onClick={() => toggleType('all')}
          aria-label={`All · ${isLoading ? '…' : total}`}
          aria-pressed={activeTypes.includes('all')}
          style={{
            height: 32,
            padding: '0 14px',
            borderRadius: 999,
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 500,
            border: '1.5px solid',
            background: activeTypes.includes('all') ? 'var(--primary-soft)' : 'transparent',
            color: activeTypes.includes('all') ? 'var(--primary)' : 'var(--ink-2)',
            borderColor: activeTypes.includes('all') ? 'var(--primary)' : 'var(--line)',
            fontFamily: 'inherit',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          All · {isLoading ? '…' : total}
        </button>

        {(
          Object.entries(EVENT_TYPES) as [
            keyof typeof EVENT_TYPES,
            (typeof EVENT_TYPES)[keyof typeof EVENT_TYPES],
          ][]
        ).map(([id, t]) => {
          const on = activeTypes.includes(id)
          return (
            <button
              key={id}
              onClick={() => toggleType(id)}
              aria-label={t.label}
              aria-pressed={on}
              style={{
                height: 32,
                padding: '0 12px',
                borderRadius: 999,
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 500,
                border: '1.5px solid',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: on ? t.tint : 'transparent',
                color: on ? t.color : 'var(--ink-2)',
                borderColor: on ? t.color : 'var(--line)',
                fontFamily: 'inherit',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: t.color,
                  flexShrink: 0,
                }}
              />
              {t.label}
            </button>
          )
        })}

        <div
          style={{
            width: 1,
            height: 22,
            background: 'var(--line)',
            margin: '0 4px',
            flexShrink: 0,
          }}
        />

        {/* Filters button + popover */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <button
            ref={filtersButtonRef}
            onClick={() => setFiltersOpen((prev) => !prev)}
            aria-label={`Filters${attributeFilterCount > 0 ? ` (${attributeFilterCount} active)` : ''}`}
            style={{
              height: 32,
              padding: '0 12px',
              borderRadius: 999,
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 500,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontFamily: 'inherit',
              background: filtersOpen ? 'var(--primary-soft)' : 'var(--surface)',
              border: `1.5px solid ${filtersOpen ? 'var(--primary)' : 'var(--line)'}`,
              color: filtersOpen ? 'var(--primary-deep)' : 'var(--ink)',
            }}
          >
            <SlidersHorizontal size={13} />
            Filters
            {attributeFilterCount > 0 && (
              <span
                style={{
                  minWidth: 18,
                  height: 18,
                  padding: '0 5px',
                  borderRadius: 999,
                  background: 'var(--primary)',
                  color: '#fff',
                  fontSize: 10.5,
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {attributeFilterCount}
              </span>
            )}
          </button>
          {filtersOpen && (
            <FiltersPopover
              anchorRef={filtersButtonRef}
              dietary={dietary}
              audience={audience}
              denomination={denomination}
              resultCount={occurrences.length}
              onApply={(d, a, den) => {
                setDietary(d)
                setAudience(a)
                setDenomination(den)
              }}
              onClose={() => setFiltersOpen(false)}
              isMobile={isMobile}
              from={from}
              to={to}
              onWhenChange={(f, t) => { setFrom(f); setTo(t) }}
            />
          )}
        </div>

        {!isMobile && <div style={{ flex: 1 }} />}

        {/* Radius slider — desktop only */}
        {!isMobile && <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <span style={{ fontSize: 12.5, color: 'var(--muted)', fontWeight: 500 }}>Radius</span>
          <div
            style={{
              position: 'relative',
              width: 90,
              height: 14,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                height: 4,
                background: 'var(--line)',
                borderRadius: 999,
                pointerEvents: 'none',
              }}
            >
              <div
                style={{
                  width: `${((radius - 1) / 99) * 100}%`,
                  height: '100%',
                  background: GRADIENT,
                  borderRadius: 999,
                }}
              />
            </div>
            <div
              style={{
                position: 'absolute',
                width: 14,
                height: 14,
                borderRadius: '50%',
                background: 'var(--surface)',
                border: '2.5px solid var(--primary)',
                pointerEvents: 'none',
                left: `calc(${((radius - 1) / 99) * 100}% - 7px)`,
              }}
            />
            <input
              type="range"
              min={1}
              max={100}
              step={1}
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
              aria-label="Search radius in miles"
              style={{
                position: 'absolute',
                inset: 0,
                opacity: 0,
                cursor: 'pointer',
                width: '100%',
                height: '100%',
                margin: 0,
              }}
            />
          </div>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--ink-2)',
              minWidth: 34,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {radius} mi
          </span>
        </div>}

        {!isMobile && <div
          style={{
            width: 1,
            height: 22,
            background: 'var(--line)',
            margin: '0 4px',
            flexShrink: 0,
          }}
        />}

        {/* Sort dropdown — desktop only */}
        {!isMobile && <div
          ref={sortRef}
          style={{
            position: 'relative',
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>Sort:</span>
          <button
            onClick={() => setSortOpen((prev) => !prev)}
            style={{
              fontSize: 12.5,
              fontWeight: 600,
              background: 'transparent',
              border: 0,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              color: 'var(--ink-2)',
              fontFamily: 'inherit',
              padding: '2px 4px',
            }}
          >
            {currentSortLabel} <ChevronDown size={12} />
          </button>
          {sortOpen && (
            <div
              style={{
                position: 'absolute',
                right: 0,
                top: '100%',
                marginTop: 6,
                background: 'var(--surface)',
                border: '1px solid var(--line)',
                borderRadius: 12,
                zIndex: 50,
                overflow: 'hidden',
                minWidth: 150,
                boxShadow: '0 8px 24px -8px rgba(31,26,46,.2)',
              }}
            >
              {SORT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => {
                    setSort(opt.value)
                    setSortOpen(false)
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '10px 14px',
                    textAlign: 'left',
                    fontSize: 13,
                    border: 0,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    background: sort === opt.value ? 'var(--primary-soft)' : 'transparent',
                    color: sort === opt.value ? 'var(--primary-deep)' : 'var(--ink)',
                    fontWeight: sort === opt.value ? 600 : 400,
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>}
      </div>

      {/* ── 3 · Active filter chips ──────────────────────────────────────────── */}
      {hasActiveFilters && (
        <div
          style={{
            padding: '8px 32px',
            borderBottom: '1px solid var(--line-soft)',
            display: 'flex',
            gap: 6,
            alignItems: 'center',
            background: 'var(--surface-2)',
            flexShrink: 0,
            flexWrap: 'wrap',
          }}
        >
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--muted)',
              marginRight: 4,
            }}
          >
            Active
          </span>
          {activeFilterChips.map((chip) => (
            <span
              key={chip.key}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                height: 26,
                padding: '0 6px 0 10px',
                borderRadius: 999,
                background: 'var(--surface)',
                border: '1px solid var(--line-soft)',
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              {chip.label}
              <button
                onClick={chip.onRemove}
                aria-label={`Remove ${chip.label} filter`}
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--muted)',
                  background: 'transparent',
                  border: 0,
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                <X size={10} />
              </button>
            </span>
          ))}
          <button
            onClick={() => {
              setDietary(new Set())
              setAudience(new Set())
              setDenomination('')
            }}
            style={{
              fontSize: 11.5,
              color: 'var(--muted)',
              background: 'transparent',
              border: 0,
              cursor: 'pointer',
              marginLeft: 4,
              fontFamily: 'inherit',
            }}
          >
            Clear all
          </button>
        </div>
      )}

      {/* ── 4 · Split content area ───────────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* Results list — hidden on mobile when map is active */}
        <div
          style={{
            width: view === 'list' || isMobile ? '100%' : 520,
            padding: isMobile ? '12px 14px' : '20px 24px',
            overflowY: 'auto',
            borderRight: (!isMobile && view === 'map') ? '1px solid var(--line-soft)' : 'none',
            flexShrink: 0,
            display: (isMobile && view === 'map') ? 'none' : 'block',
          }}
        >
          <div style={{ marginBottom: 12 }}>
            <div
              style={{
                fontSize: 10,
                letterSpacing: '0.08em',
                color: 'var(--muted)',
              }}
            >
              <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>
                {isLoading ? (
                  'Searching…'
                ) : isError ? (
                  <span style={{ color: 'var(--warn)' }}>Failed to load results</span>
                ) : (
                  `${needle ? visibleOccurrences.length : total} ${(needle ? visibleOccurrences.length : total) === 1 ? 'event' : 'events'} found`
                )}
              </span>
            </div>
          </div>

          {/* Inline text filter */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 12px',
              marginBottom: 12,
              background: 'var(--surface)',
              border: '1.5px solid var(--line-soft)',
              borderRadius: 999,
            }}
          >
            <Search size={13} color="var(--muted)" style={{ flexShrink: 0 }} />
            <input
              value={textFilter}
              onChange={(e) => setTextFilter(e.target.value)}
              placeholder="Filter by name or location…"
              aria-label="Filter results by name or location"
              style={{
                border: 0,
                background: 'transparent',
                outline: 'none',
                fontSize: 13,
                fontFamily: 'inherit',
                color: 'var(--ink)',
                flex: 1,
              }}
            />
            {textFilter && (
              <button
                onClick={() => setTextFilter('')}
                aria-label="Clear text filter"
                style={{
                  background: 'transparent',
                  border: 0,
                  cursor: 'pointer',
                  padding: 0,
                  color: 'var(--muted)',
                  display: 'flex',
                }}
              >
                <X size={12} />
              </button>
            )}
          </div>

          {isLoading && (
            <p
              style={{
                textAlign: 'center',
                fontSize: 13,
                color: 'var(--muted)',
                padding: '40px 0',
              }}
            >
              Searching…
            </p>
          )}
          {!isLoading && !isError && visibleOccurrences.length === 0 && (
            <p
              style={{
                textAlign: 'center',
                fontSize: 13,
                color: 'var(--muted)',
                padding: '40px 0',
              }}
            >
              {needle ? `No events match "${textFilter}".` : 'No events found in this area.'}
            </p>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {visibleOccurrences.map((occ, i) => (
              <OccurrenceCard
                key={occ.id}
                occurrence={occ}
                index={i + 1}
                selected={selectedId === occ.id}
                onClick={() => setSelectedId(occ.id)}
                trackImpression
              />
            ))}
          </div>
        </div>

        {/* Map pane */}
        {view === 'map' && (
          <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
            <Suspense fallback={<div style={{ height: '100%', background: '#E5DCBE' }} />}>
              <PotluckMap
                occurrences={visibleOccurrences}
                numberMap={Object.fromEntries(visibleOccurrences.map((o, i) => [o.id, i + 1]))}
                center={{ lat, lng }}
                onSelect={handleSelect}
                selectedId={selectedId}
              />
            </Suspense>
          </div>
        )}
      </div>
    </PublicLayout>
  )
}
